import React, { memo, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

/**
 * RiskInsights - Displays AI-generated insights with categories
 *
 * @param {Array} insights - Array of insight objects
 * @param {Array} projectObservations - Array of project observations for mapping IDs to names
 */
const RiskInsights = memo(({ insights, projectObservations }) => {
	const [expanded, setExpanded] = useState(true)

	// Build a map of project ID to project info for quick lookups
	const projectMap = useMemo(() => {
		if (!projectObservations) return {}
		return projectObservations.reduce((acc, obs) => {
			acc[obs.project_id] = {
				id: obs.project_id,
				name: obs.project_name
			}
			return acc
		}, {})
	}, [projectObservations])

	/**
	 * Parse text and replace "Project XX" patterns with linked project names
	 * Matches patterns like "Project 66", "project 67", "Project #66"
	 */
	const parseProjectReferences = (text) => {
		if (!text || Object.keys(projectMap).length === 0) {
			return text
		}

		// Regex to match "Project" followed by optional # and digits
		const projectPattern = /Project\s*#?(\d+)/gi
		const parts = []
		let lastIndex = 0
		let match

		while ((match = projectPattern.exec(text)) !== null) {
			const projectId = parseInt(match[1], 10)
			const project = projectMap[projectId]

			// Add text before the match
			if (match.index > lastIndex) {
				parts.push(text.substring(lastIndex, match.index))
			}

			// Add the project link or original text
			if (project) {
				parts.push(
					<Link
						key={`project-${projectId}-${match.index}`}
						to={`/projects/${projectId}`}
						className="risk-insights__project-link"
					>
						{project.name}
					</Link>
				)
			} else {
				// No matching project found, keep original text
				parts.push(match[0])
			}

			lastIndex = match.index + match[0].length
		}

		// Add remaining text after last match
		if (lastIndex < text.length) {
			parts.push(text.substring(lastIndex))
		}

		// If no matches found, return original text
		if (parts.length === 0) {
			return text
		}

		return parts
	}

	if (!insights || insights.length === 0) {
		return null
	}

	const getSeverityIcon = (severity) => {
		switch (severity?.toLowerCase()) {
			case 'high':
				return (
					<svg className="risk-insights__severity-icon risk-insights__severity-icon--high" viewBox="0 0 24 24" fill="currentColor">
						<circle cx="12" cy="12" r="10" />
					</svg>
				)
			case 'medium':
				return (
					<svg className="risk-insights__severity-icon risk-insights__severity-icon--medium" viewBox="0 0 24 24" fill="currentColor">
						<circle cx="12" cy="12" r="10" />
					</svg>
				)
			case 'low':
				return (
					<svg className="risk-insights__severity-icon risk-insights__severity-icon--low" viewBox="0 0 24 24" fill="currentColor">
						<circle cx="12" cy="12" r="10" />
					</svg>
				)
			default:
				return (
					<svg className="risk-insights__severity-icon" viewBox="0 0 24 24" fill="currentColor">
						<circle cx="12" cy="12" r="10" />
					</svg>
				)
		}
	}

	return (
		<div className="risk-insights">
			<div
				className="risk-insights__header"
				onClick={() => setExpanded(!expanded)}
				role="button"
				tabIndex={0}
				onKeyPress={(e) => e.key === 'Enter' && setExpanded(!expanded)}
			>
				<h3 className="risk-insights__title">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
					</svg>
					AI Insights
				</h3>
				<span className={`risk-insights__toggle ${expanded ? 'risk-insights__toggle--expanded' : ''}`}>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M19 9l-7 7-7-7" />
					</svg>
				</span>
			</div>

			{expanded && (
				<div className="risk-insights__content">
					{insights.map((insight, index) => (
						<div key={index} className={`risk-insights__item risk-insights__item--${insight.severity?.toLowerCase() || 'default'}`}>
							<div className="risk-insights__item-header">
								{getSeverityIcon(insight.severity)}
								<span className="risk-insights__category">{insight.category}</span>
							</div>
							<div className="risk-insights__finding">
								<span className="risk-insights__finding-bullet">&#8226;</span>
								<span>{parseProjectReferences(insight.finding)}</span>
							</div>
							{insight.recommendation && (
								<div className="risk-insights__recommendation">
									<span className="risk-insights__recommendation-arrow">&rarr;</span>
									<span><strong>Recommendation:</strong> {parseProjectReferences(insight.recommendation)}</span>
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	)
})

RiskInsights.displayName = 'RiskInsights'

RiskInsights.propTypes = {
	insights: PropTypes.arrayOf(
		PropTypes.shape({
			category: PropTypes.string,
			severity: PropTypes.string,
			finding: PropTypes.string,
			recommendation: PropTypes.string
		})
	),
	projectObservations: PropTypes.arrayOf(
		PropTypes.shape({
			project_id: PropTypes.number,
			project_name: PropTypes.string
		})
	)
}

export default RiskInsights
